import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import {
  Thermostat as TemperatureIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Notes as NotesIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { QK } from "../../../../../../base/qk";
import { getAssessmentByID, updateAssessment } from "../../../../../../api/services/observations";
import { enqueueSnackbar } from "notistack";
import CustomInput from "../../../../../CustomForm/CustomInput";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomDateField from "../../../../../FormFields/CustomDateField";
import CustomTimePicker from "../../../../../FormFields/TiimePicker/TimePicker";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";

const splitDateTime = (dateTime) => {
  if (!dateTime) return { date: "", time: "" };
  try {
    const d = new Date(dateTime);
    if (isNaN(d.getTime())) return { date: "", time: "" };
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${min}` };
  } catch {
    return { date: "", time: "" };
  }
};

const EditTemperatureModal = ({ show, handleClose, selectedID }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    value: "",
    notes: "",
    date: "",
    time: "",
    handover: false,
    manager: false,
  });

  const { data: assessment, isLoading, refetch } = useQuery(
    [QK.temperature, selectedID],
    () => getAssessmentByID(selectedID),
    {
      enabled: false,
      onSuccess: (data) => {
        if (data) {
          const { date, time } = splitDateTime(data.date_time);
          setFormData({
            value: data.value || "",
            notes: data.notes || "",
            date,
            time,
            handover: data.handover || false,
            manager: data.manager || false,
          });
        }
      },
    }
  );

  useEffect(() => {
    if (show && selectedID) {
      refetch();
    }
  }, [show, selectedID, refetch]);

  const updateMutation = useMutation(
    (newData) => updateAssessment(selectedID, newData),
    {
      onSuccess: () => {
        enqueueSnackbar("Temperature entry updated successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.temperatures]);
        queryClient.invalidateQueries([QK.temperature, selectedID]);
        handleClose();
      },
      onError: (error) => {
        enqueueSnackbar(error?.response?.data?.message || "Failed to update temperature entry", {
          variant: "error",
        });
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBox = (checked, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const combinedDateTime = formData.date && formData.time
      ? `${formData.date}T${formData.time}:00`
      : "";

    const payload = {
      value: formData.value,
      notes: formData.notes,
      date_time: combinedDateTime,
      handover: formData.handover,
      manager: formData.manager,
    };
    updateMutation.mutate(payload);
  };

  const handleDialogClose = () => {
    setFormData({
      value: "",
      notes: "",
      date: "",
      time: "",
      handover: false,
      manager: false,
    });
    handleClose();
  };

  if (isLoading) {
    return (
      <Dialog open={show} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const cardSx = {
    p: 1.75,
    borderRadius: "10px",
    border: "1px solid #EAECF0",
    bgcolor: "#FFFFFF",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
    flex: "1 1 220px",
    minWidth: 0,
  };

  const fieldHeaderSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 1.25,
  };

  const iconBoxSx = (color) => ({
    width: 26,
    height: 26,
    borderRadius: "7px",
    bgcolor: alpha(color, 0.1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  });

  const labelSx = {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#344054",
  };

  const requiredBadgeSx = {
    fontSize: "0.65rem",
    fontWeight: 600,
    color: "#F04438",
    ml: "auto",
  };

  return (
    <Dialog
      open={show}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  color: "#101828",
                  letterSpacing: "-0.01em",
                }}
              >
                Edit Temperature Entry
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", color: "#667085", mt: 0.2 }}>
                Update temperature record details
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{
              color: "#98A2B3",
              "&:hover": { bgcolor: "#F2F4F7", color: "#475467" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: "#EAECF0" }} />
      </DialogTitle>

      <form onSubmit={handleSubmit} style={{ width: "100%" }}>
        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
              mb: 2,
            }}
          >
            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#EF4444", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#EF4444", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#EF4444")}>
                  <TemperatureIcon sx={{ color: "#EF4444", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>Temperature</Typography>
                <Typography sx={requiredBadgeSx}>Required</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomInput
                  name="value"
                  type="number"
                  value={formData.value || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  placeholder="Enter temperature (°C)"
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#667085", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#667085", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#667085")}>
                  <CalendarIcon sx={{ color: "#667085", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>Date</Typography>
                <Typography sx={requiredBadgeSx}>Required</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomDateField
                  name="date"
                  label=""
                  value={formData.date || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </Box>
            </Box>

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#8B5CF6", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#8B5CF6", 0.08)}`,
                },
              }}
            >
              <Box sx={fieldHeaderSx}>
                <Box sx={iconBoxSx("#8B5CF6")}>
                  <TimeIcon sx={{ color: "#8B5CF6", fontSize: 16 }} />
                </Box>
                <Typography sx={labelSx}>Time</Typography>
                <Typography sx={requiredBadgeSx}>Required</Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomTimePicker
                  name="time"
                  label=""
                  value={formData.time || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              ...cardSx,
              flex: "1 1 100%",
              mb: 2,
              "&:hover": {
                borderColor: alpha("#F59E0B", 0.4),
                boxShadow: `0px 2px 8px ${alpha("#F59E0B", 0.08)}`,
              },
            }}
          >
            <Box sx={fieldHeaderSx}>
              <Box sx={iconBoxSx("#F59E0B")}>
                <NotesIcon sx={{ color: "#F59E0B", fontSize: 16 }} />
              </Box>
              <Typography sx={labelSx}>Notes</Typography>
            </Box>
            <CustomTextArea
              name="notes"
              value={formData.notes || ""}
              onChange={handleChange}
              minRows={2}
              maxRows={4}
              fullWidth
              placeholder="Add any notes..."
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 4,
              flexWrap: "wrap",
              p: 1.75,
              borderRadius: "10px",
              border: "1px solid #EAECF0",
              bgcolor: "#FFFFFF",
            }}
          >
            <CustomCheckbox
              label="Share with Manager"
              name="manager"
              checked={formData.manager}
              onClick={(checked) => handleCheckBox(checked, "manager")}
            />
            <CustomCheckbox
              label="Handover"
              name="handover"
              checked={formData.handover}
              onClick={(checked) => handleCheckBox(checked, "handover")}
            />
          </Box>
        </DialogContent>

        <Divider sx={{ borderColor: "#EAECF0" }} />

        <DialogActions sx={{ p: 2.5, gap: 1.5 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            disabled={updateMutation.isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              borderRadius: "8px",
              borderColor: "#D0D5DD",
              color: "#344054",
              px: 2.5,
              height: 38,
              "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateMutation.isLoading}
            startIcon={
              updateMutation.isLoading ? (
                <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
              ) : (
                <SaveIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              borderRadius: "8px",
              bgcolor: "#2469F0",
              height: 38,
              px: 3,
              boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 2px 6px rgba(36, 105, 240, 0.25)",
              },
            }}
          >
            {updateMutation.isLoading ? "Saving..." : "Update Temperature"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditTemperatureModal;